import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { FaUpload, FaVideo, FaCheckCircle, FaTimesCircle, FaCloudUploadAlt, FaLink } from 'react-icons/fa';

const VideoUpload = ({ onUploadSuccess }) => {
  const [activeTab, setActiveTab] = useState('url'); // 'url' | 'widget'
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'beginner',
    level: 1,
    tags: '',
    accessLevel: 'free',
    lesson: '',
    homework: '',
    order: 1
  });
  const [cloudinaryUrl, setCloudinaryUrl] = useState('');   // for URL tab
  const [cloudinaryPublicId, setCloudinaryPublicId] = useState('');

  const [lessons, setLessons] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [cloudFile, setCloudFile] = useState(null);
  const cloudinaryRef = useRef(null);
  const widgetRef = useRef(null);

  const API_BASE = process.env.REACT_APP_API_URL || '/api';

  // Load Cloudinary Upload Widget script once
  useEffect(() => {
    const existing = document.getElementById('cloudinary-widget-script');
    if (existing) {
      cloudinaryRef.current = window.cloudinary;
      return;
    }
    const script = document.createElement('script');
    script.id = 'cloudinary-widget-script';
    script.src = 'https://widget.cloudinary.com/v2.0/global/all.js';
    script.async = true;
    script.onload = () => { cloudinaryRef.current = window.cloudinary; };
    document.body.appendChild(script);
  }, []);

  // Fetch lessons for dropdown
  useEffect(() => {
    const fetchLessons = async () => {
      try {
        const res = await fetch(`${API_BASE}/lessons`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        const data = await res.json();
        if (data.success) setLessons(data.data);
      } catch (err) {
        console.error('Error fetching lessons:', err);
      }
    };
    fetchLessons();
  }, [API_BASE]);

  const categories = [
    { value: 'beginner', label: 'Beginner' },
    { value: 'intermediate', label: 'Intermediate' },
    { value: 'advanced', label: 'Advanced' },
    { value: 'theory', label: 'Theory' },
    { value: 'practice', label: 'Practice' },
    { value: 'performance', label: 'Performance' }
  ];

  const accessLevels = [
    { value: 'free', label: 'Free' },
    { value: 'premium', label: 'Premium' },
    { value: 'vip', label: 'VIP' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Open the Cloudinary Upload Widget
  const openCloudinaryWidget = async () => {
    if (!formData.title.trim()) {
      alert('Please enter a title before uploading the video.');
      return;
    }

    const token = localStorage.getItem('token');

    // Fetch cloud name & api key from our backend
    let cloudName, apiKey;
    try {
      const sigRes = await fetch(`${API_BASE}/videos/upload-signature`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const sigData = await sigRes.json();
      if (!sigData.success) throw new Error('Could not get Cloudinary config');
      cloudName = sigData.data.cloudName;
      apiKey = sigData.data.apiKey;
    } catch (err) {
      setUploadStatus('error: Could not connect to server: ' + err.message);
      return;
    }

    // Create widget (only once, or recreate if needed)
    widgetRef.current = cloudinaryRef.current.createUploadWidget(
      {
        cloudName,
        apiKey,
        // Signed upload: widget calls our backend to sign params
        uploadSignature: async (callback, paramsToSign) => {
          const res = await fetch(`${API_BASE}/admin/videos/widget-signature`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({ params_to_sign: paramsToSign })
          });
          const data = await res.json();
          callback(data.signature);
        },
        sources: ['local'],
        multiple: false,
        resourceType: 'video',
        folder: 'taalsetu/videos',
        clientAllowedFormats: ['mp4', 'mov', 'avi', 'wmv', 'webm', 'mkv'],
        maxFileSize: 5_000_000_000, // 5 GB
        showAdvancedOptions: false,
        cropping: false,
        showUploadMoreButton: false,
        styles: {
          palette: {
            window: '#FFFFFF',
            windowBorder: '#90A0B3',
            tabIcon: '#EA580C',
            menuIcons: '#5A616A',
            textDark: '#000000',
            textLight: '#FFFFFF',
            link: '#EA580C',
            action: '#EA580C',
            inactiveTabIcon: '#0E2F5A',
            error: '#F44235',
            inProgress: '#EA580C',
            complete: '#20B832',
            sourceBg: '#E4EBF1'
          }
        }
      },
      async (error, result) => {
        if (error) {
          console.error('Widget error:', error);
          setUploadStatus('error: ' + (error.message || 'Upload widget error'));
          setUploading(false);
          return;
        }

        if (result.event === 'upload-added') {
          setUploading(true);
          setUploadStatus('widget_open');
        }

        if (result.event === 'success') {
          const info = result.info;
          setCloudFile(info);
          widgetRef.current.close();

          // Auto-save to backend
          setUploadStatus('saving');
          try {
            const saveForm = new FormData();
            saveForm.append('cloudData', JSON.stringify({
              public_id: info.public_id,
              secure_url: info.secure_url,
              bytes: info.bytes,
              duration: info.duration || 0
            }));
            saveForm.append('title', formData.title);
            saveForm.append('description', formData.description);
            saveForm.append('category', formData.category);
            saveForm.append('level', formData.level);
            saveForm.append('tags', formData.tags);
            saveForm.append('accessLevel', formData.accessLevel);
            saveForm.append('homework', formData.homework);
            if (formData.lesson) saveForm.append('lesson', formData.lesson);
            saveForm.append('order', formData.order);

            const saveRes = await fetch(`${API_BASE}/admin/videos/upload`, {
              method: 'POST',
              headers: { Authorization: `Bearer ${token}` },
              body: saveForm
            });
            const saveData = await saveRes.json();

            if (saveData.success) {
              setUploadStatus('success');
              setFormData({ title: '', description: '', category: 'beginner', level: 1, tags: '', accessLevel: 'free', lesson: '', homework: '', order: 1 });
              setCloudFile(null);
              if (onUploadSuccess) onUploadSuccess();
            } else {
              setUploadStatus('error: ' + (saveData.message || 'Failed to save video'));
            }
          } catch (saveErr) {
            setUploadStatus('error: ' + saveErr.message);
          }
          setUploading(false);
        }

        if (result.event === 'close' && uploadStatus === 'widget_open') {
          setUploading(false);
          setUploadStatus(null);
        }
      }
    );

    widgetRef.current.open();
  };

  const isError = typeof uploadStatus === 'string' && uploadStatus.startsWith('error:');
  const isSuccess = uploadStatus === 'success';

  // --- Helpers ---
  const parseVideoUrls = (input) => {
    if (!input) return [];
    return input
      .split(/[\n,]+/)
      .flatMap(part => {
        // Also support multiple URLs separated by space if pasted together
        const links = part.match(/https?:\/\/[^\s,]+/gi);
        return links && links.length > 1 ? links : [part];
      })
      .map(u => u.trim())
      .filter(Boolean);
  };

  // Extract YouTube video ID from any YouTube URL format (including Shorts, mobile, embed, youtu.be)
  const extractYouTubeId = (url) => {
    if (!url) return null;
    const str = url.trim();

    // Direct 11-char ID
    if (/^[a-zA-Z0-9_-]{11}$/.test(str)) return str;

    const patterns = [
      // YouTube Shorts anywhere in the URL (youtube.com/shorts/... or youtu.be/shorts/...)
      /(?:youtube\.com|youtu\.be)\/shorts\/([a-zA-Z0-9_-]{11})/i,
      // Standard watch?v=...
      /youtube\.com\/watch\?.*v=([a-zA-Z0-9_-]{11})/i,
      // youtu.be/ID
      /youtu\.be\/([a-zA-Z0-9_-]{11})/i,
      // embed / live / v
      /youtube\.com\/(?:embed|live|v)\/([a-zA-Z0-9_-]{11})/i,
      // fallback parameter v=11chars
      /[?&]v=([a-zA-Z0-9_-]{11})/i,
      // fallback path with 11 chars
      /\/([a-zA-Z0-9_-]{11})(?:[?&#/]|$)/i
    ];

    for (const p of patterns) {
      const m = str.match(p);
      if (m && m[1] && m[1].length === 11) return m[1];
    }
    return null;
  };

  const isYouTubeUrl = (url) => {
    if (!url) return false;
    const lower = url.toLowerCase();
    return lower.includes('youtube.com') || lower.includes('youtu.be') || extractYouTubeId(url) !== null;
  };

  // Extract Google Drive file ID from any Drive share URL
  const extractDriveId = (url) => {
    const patterns = [
      /\/file\/d\/([a-zA-Z0-9_-]+)/,
      /id=([a-zA-Z0-9_-]+)/,
      /\/d\/([a-zA-Z0-9_-]+)/
    ];
    for (const p of patterns) {
      const m = url.match(p);
      if (m) return m[1];
    }
    return null;
  };

  const isGoogleDriveUrl = (url) =>
    url.includes('drive.google.com') || url.includes('docs.google.com');

  const processSingleUrl = (rawUrl, fallbackPubId = '') => {
    let finalUrl = rawUrl.trim();
    let pubId = fallbackPubId.trim();

    if (isYouTubeUrl(finalUrl)) {
      const ytId = extractYouTubeId(finalUrl);
      if (!ytId) {
        throw new Error(`Could not extract YouTube video ID from: "${finalUrl}". Please ensure it is a valid YouTube or Shorts link.`);
      }
      finalUrl = `https://www.youtube.com/embed/${ytId}?rel=0&modestbranding=1`;
      pubId = pubId || `youtube_${ytId}`;
      const isShort = finalUrl.toLowerCase().includes('/shorts') || rawUrl.toLowerCase().includes('/shorts');
      return { finalUrl, pubId, label: isShort ? 'YouTube Short' : 'YouTube', id: ytId };
    } else if (isGoogleDriveUrl(finalUrl)) {
      const driveId = extractDriveId(finalUrl);
      if (!driveId) {
        throw new Error(`Could not extract Google Drive file ID from: "${finalUrl}". Please check the share link.`);
      }
      finalUrl = `https://drive.google.com/file/d/${driveId}/preview`;
      pubId = pubId || driveId;
      return { finalUrl, pubId, label: 'Google Drive', id: driveId };
    } else {
      if (!pubId && finalUrl.includes('cloudinary.com')) {
        const match = finalUrl.match(/\/upload\/(?:v\d+\/)?(.+?)(?:\.[^.\/]+)?$/);
        pubId = match ? match[1] : '';
      }
      if (!pubId) {
        pubId = `video_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
      }
      return { finalUrl, pubId, label: 'Direct Video', id: pubId };
    }
  };

  // --- Tab 2: Save video by pasting YouTube, Google Drive or Cloudinary URL ---
  const handleSaveFromUrl = async () => {
    if (!formData.title.trim()) {
      alert('Please enter a Title at the top of the form.');
      setUploadStatus('error: Please enter a Title for the video.');
      return;
    }
    const rawUrls = parseVideoUrls(cloudinaryUrl);
    if (rawUrls.length === 0) {
      alert('Please paste at least one YouTube / video URL.');
      setUploadStatus('error: Please paste at least one video URL.');
      return;
    }

    let processedList = [];
    try {
      processedList = rawUrls.map(u =>
        processSingleUrl(u, rawUrls.length === 1 ? cloudinaryPublicId : '')
      );
    } catch (err) {
      setUploadStatus('error: ' + err.message);
      return;
    }

    setUploading(true);
    const token = localStorage.getItem('token');
    const total = processedList.length;
    const baseOrder = parseInt(formData.order, 10) || 1;
    const fallbackDescription = formData.description.trim() || formData.title.trim();

    try {
      for (let i = 0; i < total; i++) {
        const item = processedList[i];
        if (total > 1) {
          setUploadStatus(`saving: Saving video ${i + 1} of ${total} (${item.label})...`);
        } else {
          setUploadStatus('saving: Saving video to library...');
        }

        const videoTitle = total > 1
          ? `${formData.title.trim()} - Part ${i + 1}`
          : formData.title.trim();

        const videoOrder = baseOrder + i;

        const saveForm = new FormData();
        saveForm.append('cloudData', JSON.stringify({
          public_id: item.pubId,
          secure_url: item.finalUrl,
          bytes: 0,
          duration: 0
        }));
        saveForm.append('title', videoTitle);
        saveForm.append('description', fallbackDescription);
        saveForm.append('category', formData.category);
        saveForm.append('level', formData.level);
        saveForm.append('tags', formData.tags);
        saveForm.append('accessLevel', formData.accessLevel);
        saveForm.append('homework', formData.homework);
        if (formData.lesson) saveForm.append('lesson', formData.lesson);
        saveForm.append('order', videoOrder);

        const res = await fetch(`${API_BASE}/admin/videos/upload`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: saveForm
        });
        const data = await res.json();

        if (!data.success) {
          throw new Error(data.message || `Failed to save video ${i + 1}`);
        }
      }

      setUploadStatus('success');
      setCloudinaryUrl('');
      setCloudinaryPublicId('');
      setFormData({ title: '', description: '', category: 'beginner', level: 1, tags: '', accessLevel: 'free', lesson: '', homework: '', order: 1 });
      if (onUploadSuccess) onUploadSuccess();
    } catch (err) {
      setUploadStatus('error: ' + err.message);
    } finally {
      setUploading(false);
    }
  };


  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 transition-colors duration-500">
      <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Upload New Video</h2>
        <p className="text-gray-600 dark:text-gray-400">Add a new tabla lesson video to your course library</p>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 dark:border-gray-700">
        <button
          className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'url' ? 'border-orange-500 text-orange-600 dark:text-orange-400' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}
          onClick={() => setActiveTab('url')}
        >
          <FaLink /> Paste YouTube / Shorts / Drive Link
        </button>
        <button
          className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'widget' ? 'border-orange-500 text-orange-600 dark:text-orange-400' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}
          onClick={() => setActiveTab('widget')}
        >
          <FaCloudUploadAlt /> Upload File from Computer
        </button>
      </div>

      <div className="p-6 space-y-6">
        {/* Title */}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              disabled={uploading}
              required
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              placeholder="e.g. Teental Lesson 1 - Basic Theka"
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Category <span className="text-red-500">*</span>
            </label>
            <select
              name="category"
              value={formData.category}
              onChange={handleInputChange}
              disabled={uploading}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              {categories.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Level + Access Level */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Level (1-100) <span className="text-red-500">*</span></label>
            <input
              type="number"
              name="level"
              value={formData.level}
              onChange={handleInputChange}
              disabled={uploading}
              min="1" max="100"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Access Level</label>
            <select
              name="accessLevel"
              value={formData.accessLevel}
              onChange={handleInputChange}
              disabled={uploading}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              {accessLevels.map(al => (
                <option key={al.value} value={al.value}>{al.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Lesson + Order */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Assign to Lesson</label>
            <select
              name="lesson"
              value={formData.lesson}
              onChange={handleInputChange}
              disabled={uploading}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="">No Lesson (Standalone)</option>
              {lessons.map(lesson => (
                <option key={lesson._id} value={lesson._id}>
                  {lesson.title} — {lesson.course?.title || 'COURSE'}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Order within Lesson</label>
            <input
              type="number"
              name="order"
              value={formData.order}
              onChange={handleInputChange}
              disabled={uploading}
              min="1"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Description <span className="text-red-500">*</span>
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            disabled={uploading}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            placeholder="Describe what students will learn in this video..."
          />
        </div>

        {/* Homework */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Homework Instruction (Optional)
          </label>
          <textarea
            name="homework"
            value={formData.homework}
            onChange={handleInputChange}
            disabled={uploading}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            placeholder="Assign a homework task for the student (e.g. Play Teental slowly for 5 minutes)"
          />
        </div>

        {/* Tags */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Tags (comma-separated)</label>
          <input
            type="text"
            name="tags"
            value={formData.tags}
            onChange={handleInputChange}
            disabled={uploading}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            placeholder="tabla, rhythm, beginner, practice"
          />
        </div>

        {/* ── Tab: Use Cloudinary URL ── */}
        {activeTab === 'url' && (
          <div className="border border-blue-200 dark:border-blue-900/50 bg-blue-50 dark:bg-blue-900/10 rounded-lg p-4 space-y-4">
            <p className="text-sm font-semibold text-blue-800 dark:text-blue-300">📺 Paste a YouTube, Google Drive, or Cloudinary URL</p>

            {/* YouTube instructions */}
            <div className="bg-white dark:bg-gray-900 border border-blue-100 dark:border-blue-800 rounded-lg p-3 space-y-1">
              <p className="text-xs font-bold text-red-600 dark:text-red-400">▶ YouTube &amp; YouTube Shorts (Full Support)</p>
              <ul className="text-xs text-gray-600 dark:text-gray-400 list-disc ml-4 space-y-1">
                <li>Paste any standard YouTube link or <strong>YouTube Shorts</strong> link (e.g. <code>https://youtube.com/shorts/...</code>)</li>
                <li>Make sure the video visibility is <strong>"Unlisted"</strong> or <strong>"Public"</strong> (not Private)</li>
                <li>You can paste multiple links separated by commas to add multi-part videos together</li>
              </ul>
            </div>

            {/* Google Drive instructions */}
            <div className="bg-white dark:bg-gray-900 border border-blue-100 dark:border-blue-800 rounded-lg p-3 space-y-1">
              <p className="text-xs font-bold text-blue-600 dark:text-blue-400">📁 Google Drive (alternative)</p>
              <ol className="text-xs text-gray-600 dark:text-gray-400 list-decimal ml-4 space-y-1">
                <li>Upload to Google Drive</li>
                <li>Right-click → <strong>Share → Anyone with link → Copy</strong></li>
                <li>Paste below ↓</li>
              </ol>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Video URL(s) <span className="text-red-500">*</span>
                </label>
                <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                  Tip: Separate multiple links with commas or new lines
                </span>
              </div>
              <textarea
                value={cloudinaryUrl}
                onChange={e => setCloudinaryUrl(e.target.value)}
                disabled={uploading}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 text-sm font-mono"
                placeholder="https://www.youtube.com/watch?v=..., https://www.youtube.com/watch?v=..."
              />

              {/* Detected links preview */}
              {(() => {
                const urls = parseVideoUrls(cloudinaryUrl);
                if (urls.length === 0) return null;
                return (
                  <div className="mt-2 p-3 bg-white dark:bg-gray-800 rounded-lg border border-blue-200 dark:border-blue-700 space-y-1.5 shadow-sm">
                    <p className="text-xs font-semibold text-blue-700 dark:text-blue-300 flex items-center gap-1.5">
                      <span>🎬</span>
                      {urls.length === 1 ? '1 video link detected' : `${urls.length} video links detected (will create Part 1, Part 2...)`}
                    </p>
                    <div className="space-y-1">
                      {urls.map((u, i) => {
                        const isYt = isYouTubeUrl(u);
                        const isDrive = isGoogleDriveUrl(u);
                        const typeTag = isYt ? 'YouTube' : isDrive ? 'Google Drive' : 'Link';
                        const expectedTitle = urls.length > 1
                          ? `${formData.title.trim() || 'Video'} - Part ${i + 1}`
                          : (formData.title.trim() || 'Video');
                        const expectedOrder = (parseInt(formData.order, 10) || 1) + i;

                        return (
                          <div key={i} className="flex items-center justify-between text-xs bg-gray-50 dark:bg-gray-700/50 px-2.5 py-1.5 rounded">
                            <span className="font-medium text-gray-800 dark:text-gray-200 truncate max-w-xs">
                              {urls.length > 1 ? `Part ${i + 1}: ` : ''}{expectedTitle}
                            </span>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className="text-[11px] text-gray-500 dark:text-gray-400">Order: {expectedOrder}</span>
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${isYt ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' : isDrive ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' : 'bg-gray-200 text-gray-700'}`}>
                                {typeTag}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* Status messages */}
        {uploadStatus && uploadStatus.startsWith('saving') && (
          <div className="bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 p-4 rounded-lg flex items-center gap-2 border border-blue-100 dark:border-blue-800">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 dark:border-blue-400"></div>
            {uploadStatus.includes(':') ? uploadStatus.replace('saving:', '').trim() : 'Saving video to library...'}
          </div>
        )}

        {isSuccess && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="bg-green-50 text-green-800 p-4 rounded-lg flex items-center gap-2">
            <FaCheckCircle />
            Video(s) saved to library successfully! ✅
          </motion.div>
        )}

        {isError && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="bg-red-50 text-red-800 p-4 rounded-lg flex items-center gap-2">
            <FaTimesCircle />
            {uploadStatus.replace('error:', 'Error:')}
          </motion.div>
        )}

        {/* Action Button — changes based on active tab */}
        <div className="flex justify-end">
          {activeTab === 'widget' ? (
            <button
              type="button"
              onClick={openCloudinaryWidget}
              disabled={uploading || !formData.title.trim()}
              className="flex items-center gap-2 bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {uploading
                ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>Uploading...</>
                : <><FaCloudUploadAlt /> Select &amp; Upload Video</>}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSaveFromUrl}
              disabled={uploading}
              className="flex items-center gap-2 bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {uploading
                ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>Saving...</>
                : <><FaLink /> {parseVideoUrls(cloudinaryUrl).length > 1 ? `Save ${parseVideoUrls(cloudinaryUrl).length} Videos from URLs` : 'Save Video from URL'}</>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoUpload;